import { provide, ref, watch } from "vue";
import type { Ref } from "vue";
import { DA_SORT_KEY, type SortDir } from "../components/tableSort";
import type { TabKey } from "../types";

interface UseTableControls {
    searchQuery: Ref<string>;
    applyControls: <T>(rows: readonly T[], searchFields: string[]) => T[];
}

const readField = (row: unknown, field: string): unknown =>
    (row as Record<string, unknown>)[field];

export function useTableControls(activeTab: Ref<TabKey>): UseTableControls {
    const searchQuery = ref("");
    const sortField = ref<string | null>(null);
    const sortDir = ref<SortDir>("asc");

    const toggleSort = (field: string): void => {
        if (sortField.value === field) {
            sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
        } else {
            sortField.value = field;
            sortDir.value = "asc";
        }
    };

    provide(DA_SORT_KEY, {
        field: sortField,
        dir: sortDir,
        toggle: toggleSort,
    });

    // Reset controls when switching tab so filters don't leak across data sets.
    watch(activeTab, (): void => {
        searchQuery.value = "";
        sortField.value = null;
        sortDir.value = "asc";
    });

    const applyControls = <T>(
        rows: readonly T[],
        searchFields: string[],
    ): T[] => {
        const query = searchQuery.value.trim().toLowerCase();

        let result: T[] =
            query !== ""
                ? rows.filter((row) =>
                      searchFields.some((field) => {
                          const value = readField(row, field);
                          return (
                              value !== null &&
                              value !== undefined &&
                              String(value).toLowerCase().includes(query)
                          );
                      }),
                  )
                : [...rows];

        const field = sortField.value;
        if (field !== null) {
            const direction = sortDir.value === "asc" ? 1 : -1;
            result = [...result].sort((a, b) => {
                const av = readField(a, field);
                const bv = readField(b, field);

                if (typeof av === "number" && typeof bv === "number") {
                    return (av - bv) * direction;
                }

                const as = av === null || av === undefined ? "" : String(av);
                const bs = bv === null || bv === undefined ? "" : String(bv);
                return (
                    as.localeCompare(bs, undefined, { numeric: true }) *
                    direction
                );
            });
        }

        return result;
    };

    return { searchQuery, applyControls };
}
